/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { OnInit, ViewChild } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import {
  DecoratingReadOnlySelectionModel,
  MutableSelectionModel,
  newSelectionModel,
  SelectionModel,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { SlicedDataSource, SlicedDataQuery, SlicedData, EMPTY_SLICED_DATA } from '@gh/core-data';
import { DialogInput } from '@gh/core-ui';
import {
  getRowIdFromData,
  GridRowEvent,
  ManualGridDefinition,
  safeGridOptionCallback,
  GridInfiniteScrollDirective,
} from '@gh/core-ui-grid';
import { coalesce, isNotNil } from '@gh/core-util';
import { GridOptions } from 'ag-grid-community';
import { ROW_DISABLED_CLASS } from '../grid';
import { Observable } from 'rxjs';
import { of$ } from '@gh/rx';

export interface FinderDialogConfig {
  title?: string;
  applyButtonLabel?: string;
  preselected?: ID[];
  multiselection?: boolean;
  selectionModel?: SelectionModel<any>;
  unselectable?: boolean;
  isNumericSelector?: boolean;

  dataSourceFactory?(filter: any): SlicedDataSource<any>;
}

export abstract class AbstractFinderGridDefinition extends ManualGridDefinition {
  selectionModel: SelectionModel<any>;
  multiselection: boolean;
  unselectable: boolean;

  abstract getId: TransformFn<any, ID>;

  buildGridOptions(): GridOptions {
    return {
      getRowId: getRowIdFromData(this.getId),
      ...(this.unselectable ? {} : {
        rowClassRules: {
          [ROW_DISABLED_CLASS]: safeGridOptionCallback(
            ({ data }: { data: any }) => this.selectionModel.hasOld(this.getId(data)),
            false,
          ),
        },
      }),
    };
  }

}

class FinderDialogDataSourceWrapper<T> implements SlicedDataSource<T> {
  delegate?: SlicedDataSource<T>;

  select(query: SlicedDataQuery): Observable<SlicedData<T>> {
    return this.delegate ? this.delegate.select(query) : of$(EMPTY_SLICED_DATA);
  }
}

export abstract class AbstractFinderDialogComponent<DialogConfig extends FinderDialogConfig, ListItem>
  implements OnInit, DialogInput<DialogConfig> {

  input: DialogConfig;

  dataSource = new FinderDialogDataSourceWrapper<ListItem>();

  defaultTitle: string;

  @ViewChild(GridInfiniteScrollDirective, { static: false }) gridInfiniteScroll?: GridInfiniteScrollDirective;

  protected abstract getId: TransformFn<ListItem, ID>;
  protected abstract gridDefinition: AbstractFinderGridDefinition;

  protected selectionModel: MutableSelectionModel<ListItem>;

  constructor(private _dialogRef: MatDialogRef<any>) {

  }

  get title(): Maybe<string> {
    return this.input?.title || this.defaultTitle;
  }

  get applyButtonLabel(): Maybe<string> {
    return this.input?.applyButtonLabel || 'lbl_add_selected';
  }

  get hasSelected(): boolean {
    return this.selectionModel?.hasChanges();
  }

  get multiselection(): boolean {
    return coalesce(this.input?.multiselection, false);
  }

  get isNumericSelector(): boolean {
    return coalesce(this.input?.isNumericSelector, false)
  }

  get selectionSize(): number {
    return this.input?.unselectable ? this.selectionModel?.size() : this.selectionModel?.getNewIds().length;
  }

  ngOnInit(): void {
    const baseModel = this.input?.selectionModel || newSelectionModel(this.input?.preselected || []);
    this.selectionModel = new MutableSelectionModel(baseModel, this.getId);

    this.gridDefinition.selectionModel = this.selectionModel;
    this.gridDefinition.multiselection = this.multiselection;
    this.gridDefinition.unselectable = this.input.unselectable || false;

    this.loadData();
  }

  onSearch(): void {
    if (!this.multiselection) {
      this.selectionModel?.clear();
    }
    this.loadData();
  }

  onRowClicked(event: GridRowEvent<ListItem>): void {
    if (this.multiselection) {
      this.toggle(event.data);
    } else {
      this.singleSelect(event.data);
    }
  }

  onToggle(row: ListItem): void {
    this.toggle(row);
  }

  onSingleSelect(row: ListItem): void {
    this.singleSelect(row);
  }

  onApply(): void {
    this._dialogRef.close(new DecoratingReadOnlySelectionModel(this.selectionModel));
  }

  onCancel(): void {
    this._dialogRef.close(void 0);
  }

  isDisabled(row?: ListItem): boolean {
    return !this.input?.unselectable && row && this.isPreselected(row) || false;
  }

  isSelected(row?: ListItem): boolean {
    return isNotNil(row)
      && (this.input?.multiselection && this.selectionModel?.has(this.getId(row)) || this.isSelectedNow(row));
  }

  changeCount(row: ListItem, count: number): void{
    if(!row) return;
    this.selectionModel.updateCount(this.getId(row), count)
  }

  protected isSelectable?(item: ListItem): boolean;

  protected abstract buildDataSource(filter?: any): SlicedDataSource<ListItem>;

  protected getDataSourceFilter(): any {
    return;
  }

  private loadData(): void {
    const { dataSourceFactory } = this.input;
    const newSource = dataSourceFactory
      ? dataSourceFactory(this.getDataSourceFilter())
      : this.buildDataSource(this.getDataSourceFilter());

    this.dataSource.delegate = newSource;
    if (this.gridInfiniteScroll) {
      this.gridInfiniteScroll.refresh();
    }
  }

  private isSelectedNow(row: ListItem): boolean {
    return this.selectionModel?.hasNew(this.getId(row));
  }

  private singleSelect(row: ListItem): void {
    if (!this.canSelect(row) || this.isPreselected(row)) {
      return;
    }

    const selectedId = this.selectionModel.getNewIds()[0];
    if (selectedId) {
      this.selectionModel?.remove(selectedId);
    }
    this.selectionModel?.add(row);
  }

  private toggle(row: ListItem): void {
    if (!this.canSelect(row)) {
      return;
    }

    if (this.isSelected(row)) {
      if (this.input.unselectable) {
        this.unselect(row);
      } else if (!this.isPreselected(row)) {
        this.unselect(row);
      }
    } else {
      this.select(row);
    }
  }

  private select(row: ListItem): void {
    this.selectionModel?.add(row);
  }

  private unselect(row: ListItem): void {
    this.selectionModel?.remove(this.getId(row));
  }

  private isPreselected(row: ListItem): boolean {
    return this.selectionModel?.hasOld(this.getId(row));
  }

  private canSelect(row: ListItem): boolean {
    return this.isSelectable ? this.isSelectable(row) : true;
  }
}
